import {Dialog, RadioGroup, Transition} from '@headlessui/react';
import {CheckCircleIcon} from '@heroicons/react/24/solid';
import React from 'react';
import Select from 'react-select';
import {Tooltip} from 'react-tooltip';

import {Translator, useTranslations} from 'src/components/TranslationsContext';
import {
  AlertConfigType,
  ApiAlertConfig,
  ForecastThresholdAlertConfigProperties,
  UpcomingEventAlertConfigProperties,
} from 'src/lib/api/alert_configs';
import {ApiUserProfile} from 'src/lib/api/auth';
import {ApiNotificationPreference} from 'src/lib/api/preferences';
import {Project, SeriesType, Site} from 'src/lib/api/project';
import {
  AllColumns,
  ColumnDisplay,
  ColumnsBySource,
  FlowForecastColumns,
  Source,
} from 'src/lib/api/series';
import {
  HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON,
  HYDROFORECAST_ALERT_TYPE_THRESHOLD,
  HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT,
} from 'src/lib/constants';
import * as tw from 'src/lib/tailwindClasses';
import {THRESHOLD_PLOT_COLORS, THRESHOLD_PLOT_COLOR_NAMES} from 'src/lib/thresholdPlotColors';

type AlertTypeConfig = {
  displayName: (t: Translator) => string;
  description: (t: Translator) => string;
  isSourceAllowed: (source: Source) => boolean;
  unitsDisplay: (project: Project, column: AllColumns, _t: Translator) => string;
};

const AlertTypeConfigs: Record<AlertConfigType, AlertTypeConfig> = {
  [HYDROFORECAST_ALERT_TYPE_THRESHOLD]: {
    displayName: (t) => t('threshold'),
    description: (t) => t('thresholdAlertDescription'),
    // remove seasonal-all-traces as an option: complicates column selection and not needed
    isSourceAllowed: (source) => source !== Source.HydroForecastSeasonalAllTraces,
    unitsDisplay: (project, column, _t) => project.units[column],
  },
  [HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON]: {
    displayName: (t) => `${t('ltaAlert')}`,
    description: (t) => t('ltaAlertDescription'),
    isSourceAllowed: (source) =>
      [Source.HydroForecastShortTerm, Source.HydroForecastSeasonal].includes(source),
    unitsDisplay: (_project, _column, t) => t('percentLTALong'),
  },
  [HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT]: {
    displayName: (t) => `${t('upcomingEvent')}`,
    description: (t) => t('upcomingEventAlertDescription'),
    isSourceAllowed: (source) => source === Source.HydroForecastShortTerm,
    unitsDisplay: (_project, _column, _t) => '', // We don't display units for upcoming events
  },
};

type SelectionState = {
  project: Project;
  sites: Site[];
  alertType: AlertConfigType;
  source: Source | ''; // Use empty string to indicate no source selected instead of undefined since empty string works better with HTML select
  column: AllColumns | undefined;
  direction: 'above' | 'below';
  threshold: number | undefined;
  noise_floor_percent_lta?: number;
  ccEmails: string[];
  rateVolumeMode?: 'rate' | 'level';
  label: string;
  showOnPlot: 'self' | 'everyone' | null;
  plotColor: string;
};

export interface UpcomingEventSelectionState extends SelectionState {
  alertType: typeof HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT;
  direction: 'above';
  noise_floor_percent_lta: number;
  threshold: number;
  column: AllColumns;
  source: Source;
}

export interface ForecastThresholdSelectionState extends SelectionState {
  alertType:
    | typeof HYDROFORECAST_ALERT_TYPE_THRESHOLD
    | typeof HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON;
  direction: 'above' | 'below';
  threshold: number;
  column: AllColumns;
  source: Source;
}

type CompletedSelectionState = {
  [k in keyof SelectionState]: Exclude<SelectionState[k], undefined | ''>;
};

type Props = {
  projects: Project[];
  isOpen: boolean;
  close: () => void;
  submit: (
    state: CompletedSelectionState,
    editAlertConfigId?: ApiAlertConfig['id'],
    pref?: ApiNotificationPreference | null
  ) => Promise<any>;
  editAlertConfig: ApiAlertConfig | null;
  editPref: ApiNotificationPreference | null;
  currentUser: ApiUserProfile;
};

function makeSelectionState(
  editAlertConfig: ApiAlertConfig | null,
  projects: Project[],
  editPref: ApiNotificationPreference | null
): SelectionState {
  if (editAlertConfig) {
    const project = projects.find((p) => editAlertConfig.projectId === p.id);
    const sites = project?.sites!.filter((s) => editAlertConfig.siteIds.includes(s.id));
    if (project && sites) {
      if (editAlertConfig.alertType === HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT) {
        const properties = editAlertConfig.properties as UpcomingEventAlertConfigProperties;
        return {
          project,
          sites,
          alertType: editAlertConfig.alertType,
          source: properties.source,
          column: properties.column,
          direction: 'above',
          noise_floor_percent_lta: properties.noise_floor_percent_lta,
          threshold: properties.threshold,
          ccEmails: editPref?.ccEmails || [],
          label: properties.label || '',
          showOnPlot: null,
          plotColor: THRESHOLD_PLOT_COLORS[0],
        };
      } else {
        const properties = editAlertConfig.properties as ForecastThresholdAlertConfigProperties;
        return {
          project,
          sites,
          alertType: editAlertConfig.alertType,
          source: properties.source,
          column: properties.column,
          direction: properties.direction,
          threshold: properties.threshold,
          rateVolumeMode: properties.rateVolumeMode || 'rate',
          ccEmails: editPref?.ccEmails || [],
          label: properties.label || '',
          showOnPlot: properties.showOnPlot || null,
          plotColor: properties.plotColor || THRESHOLD_PLOT_COLORS[0],
        };
      }
    }
  }
  return {
    project: projects[0],
    sites: [],
    alertType: HYDROFORECAST_ALERT_TYPE_THRESHOLD,
    source: '',
    column: undefined,
    direction: 'above',
    threshold: undefined,
    ccEmails: [],
    label: '',
    showOnPlot: null,
    plotColor: THRESHOLD_PLOT_COLORS[0],
  };
}

export default function NewThresholdDialog({
  projects,
  isOpen,
  close,
  submit,
  editAlertConfig,
  editPref,
  currentUser,
}: Props) {
  const {t} = useTranslations();
  const [selection, setSelection] = React.useReducer(
    (state: SelectionState, action: Partial<SelectionState>): SelectionState => {
      if (action.project) {
        return {...state, ...action, sites: []};
      }
      return {...state, ...action};
    },
    makeSelectionState(editAlertConfig, projects, editPref)
  );
  const [currentPage, setCurrentPage] = React.useState(0);

  type RequiredKeys<T> = {[K in keyof T]-?: undefined extends T[K] ? never : K}[keyof T];

  // TODO: check with Maya about this approach.
  // The robits tell me it needs to be this verbose, but I'd love to
  // talk through it.
  const isFormComplete =
    selection.alertType === HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT
      ? ((): boolean => {
          const requiredFields: RequiredKeys<UpcomingEventSelectionState>[] = [
            'project',
            'sites',
            'alertType',
            'source',
            'column',
            'threshold',
            'noise_floor_percent_lta',
          ];
          return requiredFields.every(
            (field) =>
              selection[field] !== undefined &&
              selection[field] !== null &&
              selection[field] !== '' &&
              !(Array.isArray(selection[field]) && selection[field].length === 0)
          );
        })()
      : ((): boolean => {
          const requiredFields: RequiredKeys<ForecastThresholdSelectionState>[] = [
            'project',
            'sites',
            'alertType',
            'source',
            'column',
            'direction',
            'threshold',
          ];
          return requiredFields.every(
            (field) =>
              selection[field] !== undefined &&
              selection[field] !== null &&
              selection[field] !== '' &&
              !(Array.isArray(selection[field]) && selection[field].length === 0)
          );
        })();

  const isEditing = !!editAlertConfig?.id;

  interface Page {
    content: JSX.Element;
    canAdvance: boolean;
  }
  const pages: Page[] = [];

  if (!isEditing) {
    pages.push({
      content: (
        <SelectAlertType
          selection={selection}
          setSelection={setSelection}
          currentUser={currentUser}
        />
      ),
      canAdvance: !!selection.alertType,
    });
  }

  if (selection.alertType !== HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT) {
    pages.push({
      content: (
        <SelectSourceAndColumn
          selection={selection}
          setSelection={setSelection}
          projects={projects}
        />
      ),
      canAdvance: !!(selection.source && selection.column),
    });
    pages.push({
      content: (
        <SetThreshold selection={selection} setSelection={setSelection} projects={projects} />
      ),
      canAdvance: selection.threshold !== undefined && !isNaN(selection.threshold),
    });
  }

  pages.push({
    content: (
      <SelectSites
        projects={projects}
        selection={selection}
        setSelection={setSelection}
        isEditing={isEditing}
        currentUser={currentUser}
      />
    ),
    canAdvance: isFormComplete,
  });

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-10" onClose={close}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  {editAlertConfig?.id ? t('editAlert') : t('setUpAlert')}
                </Dialog.Title>
                {pages[currentPage].content}
                <div className="mt-4 flex gap-4">
                  {currentPage > 0 && (
                    <button
                      type="button"
                      className={tw.buttonSecondary}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      {t('back')}
                    </button>
                  )}
                  {currentPage < pages.length - 1 && (
                    <button
                      type="button"
                      className={tw.buttonPrimary}
                      disabled={!pages[currentPage].canAdvance}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      {t('continue')}
                    </button>
                  )}
                  {currentPage === pages.length - 1 && (
                    <button
                      type="button"
                      className={tw.buttonPrimary}
                      disabled={!(pages[currentPage].canAdvance && isFormComplete)}
                      onClick={async () => {
                        if (isFormComplete) {
                          await submit(
                            selection as CompletedSelectionState,
                            editAlertConfig?.id,
                            editPref
                          );
                          close();
                        }
                      }}
                    >
                      {t('submit')}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function SelectAlertType({
  selection,
  setSelection,
  currentUser,
}: {
  selection: SelectionState;
  setSelection: React.Dispatch<Partial<SelectionState>>;
  currentUser: ApiUserProfile;
}) {
  const {t} = useTranslations();

  return (
    <>
      <div className="mt-2 space-y-2">
        <RadioGroup
          value={selection.alertType}
          onChange={(alertType) => {
            if (alertType === HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT) {
              setSelection({
                alertType,
                source: Source.HydroForecastShortTerm,
                column: FlowForecastColumns.DISCHARGE_MEAN,
                direction: 'above',
                threshold: 2,
                noise_floor_percent_lta: 50,
                rateVolumeMode: undefined,
              });
            } else {
              setSelection({
                alertType,
                source: '',
                column:
                  alertType === HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON
                    ? FlowForecastColumns.DISCHARGE_MEAN
                    : undefined,
                threshold: undefined,
                direction: 'above',
                rateVolumeMode: undefined,
              });
            }
          }}
          aria-label="Alert type"
        >
          <div className="space-y-3">
            {Object.entries(AlertTypeConfigs).map(([alertType, config]) => {
              return (
                <RadioGroup.Option
                  key={alertType}
                  value={alertType}
                  className={({checked}) =>
                    `${checked ? 'ring ring-blue-500' : ''} relative flex cursor-pointer rounded-lg px-5 py-4 shadow-md focus:outline-none`
                  }
                >
                  {({checked}) => (
                    <>
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center mr-2">
                          <div className="text-sm">
                            <RadioGroup.Label as="p" className="font-medium text-gray-900 mb-1">
                              {config.displayName(t)}
                            </RadioGroup.Label>
                            <RadioGroup.Description as="span" className={'inline text-gray-500'}>
                              {config.description(t)}
                            </RadioGroup.Description>
                          </div>
                        </div>

                        <div className="shrink-0 text-blue-500 h-6 w-6">
                          {checked && <CheckCircleIcon className="h-6 w-6" />}
                        </div>
                      </div>
                    </>
                  )}
                </RadioGroup.Option>
              );
            })}
          </div>
        </RadioGroup>
      </div>
    </>
  );
}

/**
 * The project for a threshold alert is only chosen on a later step
 * (SelectSites), so until the user has explicitly picked sites we draw the
 * available sources from every project's sites rather than just the default
 * project. Otherwise a source that only exists in a non-default project (e.g.
 * seasonal) would never appear in the source dropdown.
 */
export function getSitesToCheckForSources(projects: Project[], selectedSites: Site[]): Site[] {
  return selectedSites.length > 0
    ? selectedSites
    : projects.flatMap((project) => project.sites ?? []);
}

function SelectSourceAndColumn({
  selection,
  setSelection,
  projects,
}: {
  selection: SelectionState;
  setSelection: React.Dispatch<Partial<SelectionState>>;
  projects: Project[];
}) {
  const {t} = useTranslations();

  const sitesToCheckForSources = getSitesToCheckForSources(projects, selection.sites);

  const allSources = [
    ...new Set(
      sitesToCheckForSources
        .map((site) =>
          Object.values(site.forecastTimeSeries)
            .flat()
            .map((ts) => ts.source)
        )
        .flat()
    ),
  ];

  const isHydroforecastSource =
    selection.source === Source.HydroForecastShortTerm ||
    selection.source === Source.HydroForecastSeasonal;
  // Mirror the source scope above so the level toggle reflects the same sites the
  // sources were drawn from, not just the default project.
  const projectHasLevelSites = sitesToCheckForSources.some((site) =>
    site.seriesTypes.includes(SeriesType.Level)
  );
  const showLevelToggle =
    selection.alertType === HYDROFORECAST_ALERT_TYPE_THRESHOLD &&
    isHydroforecastSource &&
    projectHasLevelSites;

  return (
    <>
      <div className="mt-2 space-y-2">
        {selection.alertType !== HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT && (
          <>
            <div>
              <label htmlFor="source" className="block text-sm font-medium leading-6 text-gray-900">
                {t('source')}
              </label>
              <Select
                id="source"
                placeholder={`${t('select')}...`}
                value={
                  selection.source ? {label: selection.source, value: selection.source} : undefined
                }
                onChange={(selectedOption) => {
                  // Reset the column if it is not available for the new source
                  const newSource = selectedOption!.value;
                  const newColumn =
                    selection.column &&
                    Object.values(ColumnsBySource[newSource] || {}).includes(selection.column)
                      ? selection.column
                      : undefined;
                  // Reset rateVolumeMode when source changes to non-hydroforecast
                  const isNewSourceHydroforecast =
                    newSource === Source.HydroForecastShortTerm ||
                    newSource === Source.HydroForecastSeasonal;
                  setSelection({
                    source: newSource,
                    column: newColumn,
                    ...(!isNewSourceHydroforecast &&
                      selection.rateVolumeMode === 'level' && {
                        rateVolumeMode: undefined,
                      }),
                  });
                }}
                options={allSources
                  .filter((source) => AlertTypeConfigs[selection.alertType].isSourceAllowed(source))
                  .map((source) => ({
                    label: source,
                    value: source,
                  }))}
              />
            </div>
            {showLevelToggle && (
              <div>
                <label
                  htmlFor="rateVolumeMode"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  {t('measurement')}
                </label>
                <Select
                  id="rateVolumeMode"
                  value={{
                    label: selection.rateVolumeMode === 'level' ? t('waterLevel') : t('flow'),
                    value: selection.rateVolumeMode || 'rate',
                  }}
                  onChange={(selectedOption) => {
                    const newMode = selectedOption!.value as 'rate' | 'level';
                    if (newMode === 'level') {
                      // Filter out sites that don't support level
                      const levelSites = selection.sites.filter((site) =>
                        site.seriesTypes.includes(SeriesType.Level)
                      );
                      setSelection({rateVolumeMode: newMode, sites: levelSites});
                    } else {
                      setSelection({rateVolumeMode: newMode});
                    }
                  }}
                  options={[
                    {label: t('flow'), value: 'rate'},
                    {label: t('waterLevel'), value: 'level'},
                  ]}
                />
              </div>
            )}
            {selection.alertType !== HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON && (
              <div>
                <label
                  htmlFor="column"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  {t('column')}
                </label>

                <Select
                  id="column"
                  placeholder={`${t('select')}...`}
                  value={
                    selection.column
                      ? {
                          label: ColumnDisplay[selection.column] || selection.column,
                          value: selection.column,
                        }
                      : undefined
                  }
                  onChange={(selectedOption) => {
                    setSelection({column: selectedOption!.value});
                  }}
                  isDisabled={!selection.source}
                  options={
                    selection.source
                      ? (
                          Object.values(ColumnsBySource[selection.source] || []) as AllColumns[]
                        ).map((col) => ({
                          label: ColumnDisplay[col] || col,
                          value: col,
                        }))
                      : []
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function generateDefaultLabel(
  selection: SelectionState,
  projects: Project[],
  t: Translator
): string {
  if (!selection.column || selection.threshold === undefined || isNaN(selection.threshold))
    return '';
  const project = selection.project || projects[0];
  const isLevel = selection.rateVolumeMode === 'level';
  const columnDisplayName = capitalize(ColumnDisplay[selection.column] || selection.column);
  const measurement = isLevel ? `${columnDisplayName} ${t('waterLevel')}` : columnDisplayName;
  const dir = selection.direction === 'above' ? '>' : '<';
  const units = isLevel
    ? String(project.unitSystem).startsWith('US')
      ? 'ft'
      : 'm'
    : project.units[selection.column] || '';
  return `${measurement} ${dir} ${selection.threshold} ${units}`.trim();
}

function SetThreshold({
  selection,
  setSelection,
  projects,
}: {
  selection: SelectionState;
  setSelection: React.Dispatch<Partial<SelectionState>>;
  projects: Project[];
}) {
  const {t} = useTranslations();
  return (
    <>
      <div className="mt-2 space-y-2"></div>
      <div>
        <label htmlFor="aboveOrBelow" className="block text-sm font-medium leading-6 text-gray-900">
          {t('direction')}
        </label>
        <Select
          id="aboveOrBelow"
          value={{
            label: selection.direction === 'above' ? t('above') : t('below'),
            value: selection.direction,
          }}
          onChange={(selectedOption) => setSelection({direction: selectedOption!.value})}
          options={[
            {label: t('above'), value: 'above'},
            {label: t('below'), value: 'below'},
          ]}
        />
      </div>

      <div>
        <label htmlFor="thresholdValue" className={tw.formInputLabel}>
          {t('threshold')}
        </label>
        <div className="flex items-center">
          <input
            id="thresholdValue"
            type="number"
            className={`${tw.formInputBase} w-1/2`}
            placeholder={t('thresholdPlaceholder')}
            value={selection.threshold}
            onChange={(e) => {
              setSelection({threshold: parseFloat(e.target.value)});
            }}
          />

          {selection.column && (
            <span className="ml-2">
              {selection.rateVolumeMode === 'level'
                ? String((selection.project || projects[0]).unitSystem).startsWith('US')
                  ? 'ft'
                  : 'm'
                : AlertTypeConfigs[selection.alertType].unitsDisplay(
                    selection.project || projects[0],
                    selection.column,
                    t
                  )}
            </span>
          )}
        </div>
      </div>

      {selection.alertType === HYDROFORECAST_ALERT_TYPE_THRESHOLD && (
        <>
          <div className="mt-4">
            <label htmlFor="alertLabel" className={tw.formInputLabel}>
              {t('alertLabel')}
            </label>
            <input
              id="alertLabel"
              type="text"
              className={tw.formInputBase}
              placeholder={t('alertLabelPlaceholder')}
              value={selection.label}
              onChange={(e) => setSelection({label: e.target.value})}
              onFocus={(e) => {
                if (!e.target.value) {
                  setSelection({label: generateDefaultLabel(selection, projects, t)});
                }
              }}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="showOnPlot"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              {t('showOnChart')}
            </label>
            <Select
              id="showOnPlot"
              value={{
                label:
                  selection.showOnPlot === 'self'
                    ? t('showForMe')
                    : selection.showOnPlot === 'everyone'
                      ? t('showForEveryone')
                      : t('dontShow'),
                value: selection.showOnPlot,
              }}
              onChange={(selectedOption) => setSelection({showOnPlot: selectedOption!.value})}
              options={[
                {label: t('dontShow'), value: null},
                {label: t('showForMe'), value: 'self'},
                {label: t('showForEveryone'), value: 'everyone'},
              ]}
            />
          </div>
          {selection.showOnPlot && (
            <div className="mt-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">
                {t('lineColor')}
              </label>
              <div className="mt-1 flex flex-wrap gap-2">
                {THRESHOLD_PLOT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`h-6 w-6 rounded-full border-2 ${
                      selection.plotColor === color ? 'border-gray-900' : 'border-transparent'
                    }`}
                    style={{backgroundColor: color}}
                    onClick={() => setSelection({plotColor: color})}
                    aria-label={THRESHOLD_PLOT_COLOR_NAMES[color] || color}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {selection.alertType === HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON && (
        <div className="mt-4 bg-blue-100 bg-opacity-50 py-3 px-4">
          <p>{t('examples')}</p>
          <ul className="list-disc list-inside mt-1">
            <li>
              {t('above')}{' '}
              {t('ltaExample', {
                percent: '250',
                units: selection.project?.units[selection.column!],
                threshold: '25',
              })}{' '}
              {t('orHigher')}
              {'.'}
            </li>
            <li>
              {t('above')}{' '}
              {t('ltaExample', {
                percent: '150',
                units: selection.project?.units[selection.column!],
                threshold: '15',
              })}{' '}
              {t('orHigher')}
              {'.'}
            </li>
            <li>
              {t('below')}{' '}
              {t('ltaExample', {
                percent: '50',
                units: selection.project?.units[selection.column!],
                threshold: '5',
              })}{' '}
              {t('orLower')}
              {'.'}
            </li>
          </ul>
        </div>
      )}
    </>
  );
}

function SelectSites({
  projects,
  selection,
  setSelection,
  isEditing,
  currentUser,
}: {
  projects: Project[];
  selection: SelectionState;
  setSelection: React.Dispatch<Partial<SelectionState>>;
  isEditing: boolean;
  currentUser: ApiUserProfile;
}) {
  const {t} = useTranslations();
  const [isRecipientsOpen, setIsRecipientsOpen] = React.useState(selection.ccEmails.length > 0);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const emailInputRef = React.useRef<HTMLInputElement>(null);

  const _siteHasSelectedSource = (site: Site) => {
    const hasSource = Object.values(site.forecastTimeSeries)
      .flat()
      .map((ts) => ts.source)
      .includes(selection.source as Source);
    if (selection.rateVolumeMode === 'level') {
      return hasSource && site.seriesTypes.includes(SeriesType.Level);
    }
    return hasSource;
  };

  const _make_project_option = (project: Project) => {
    const isDisabled = !project.sites!.some((site) => _siteHasSelectedSource(site));
    return {
      label: isDisabled ? (
        <>
          <a
            data-tooltip-id="disabled-project-tooltip"
            data-tooltip-content={`${t('projectHasNoSitesWithDataFor')} ${selection.source}`}
          >
            {project.name}
          </a>
          <Tooltip id="disabled-project-tooltip" />
        </>
      ) : (
        project.name
      ),
      value: project,
      isDisabled,
    };
  };
  const projectOptions = projects.map((project) => _make_project_option(project));

  const _make_site_option = (site: Site) => {
    const isDisabled = !_siteHasSelectedSource(site);
    const disabledReason =
      selection.rateVolumeMode === 'level' && !site.seriesTypes.includes(SeriesType.Level)
        ? `${site.name} ${t('doesNotSupportWaterLevel')}`
        : `${t('siteDoesNotHaveDataFor')} ${selection.source}`;
    return {
      label: isDisabled ? (
        <>
          <a data-tooltip-id="disabled-site-tooltip" data-tooltip-content={disabledReason}>
            {site.name}
          </a>
          <Tooltip id="disabled-site-tooltip" />
        </>
      ) : (
        site.name
      ),
      value: site,
      isDisabled,
    };
  };
  const selectedProject = selection.project || projects[0];
  const siteOptions = selectedProject.sites!.map((site) => _make_site_option(site));

  const addEmail = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const input = emailInputRef.current;
    if (!input) return;

    const trimmedEmail = input.value.trim().toLowerCase();
    if (!trimmedEmail) return;

    if (selection.ccEmails.includes(trimmedEmail)) {
      setEmailError(t('duplicateEmail'));
      return;
    }

    if (trimmedEmail === currentUser.email) {
      setEmailError(t('cannotAddYourself'));
      return;
    }

    // Use the browser's built-in email validation
    if (input.validity.valid) {
      setSelection({ccEmails: [...selection.ccEmails, trimmedEmail]});
      input.value = '';
      setEmailError(null);
    } else {
      setEmailError(t('invalidEmailFormat'));
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setSelection({ccEmails: selection.ccEmails.filter((email) => email !== emailToRemove)});
  };

  return (
    <>
      <div className="mt-2">
        <label htmlFor="project" className="block text-sm font-medium leading-6 text-gray-900">
          {t('project')}
        </label>
        <Select
          options={projectOptions}
          name="projects"
          value={_make_project_option(selectedProject)}
          onChange={(selectedOption) => {
            const selectedProject = selectedOption!.value;
            if (selectedProject !== selection.project) {
              setSelection({project: selectedProject, sites: []});
            }
          }}
          isDisabled={isEditing}
        />
      </div>
      <div className="mt-2">
        <label htmlFor="sites" className="block text-sm font-medium leading-6 text-gray-900">
          {t('sites')}
        </label>
        <Select
          classNames={{
            multiValueLabel: () =>
              'flex items-center bg-gray-200 text-gray-800 px-2 py-1 rounded-sm text-sm',
            multiValueRemove: () => 'text-gray-600 hover:text-gray-800 font-bold',
          }}
          isMulti
          name="sites"
          options={siteOptions}
          isOptionDisabled={(option) => option.isDisabled}
          placeholder={`${t('select')}...`}
          onChange={(selectedOptions) =>
            setSelection({sites: selectedOptions.map((selectedOption) => selectedOption!.value)})
          }
          value={selection.sites.map((site) => _make_site_option(site))}
        />
        <button
          type="button"
          className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
          onClick={() =>
            setSelection({
              sites: siteOptions.filter((site) => !site.isDisabled).map((site) => site.value),
            })
          }
        >
          <span className="mr-1">+</span>
          {t('selectAllAvailableSites')}
        </button>
      </div>
      <div className="mt-4 space-y-2 bg-blue-100 bg-opacity-50 py-3 px-4">
        {selection.alertType !== HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT ? (
          <p>
            {t('alertSummaryPrefix')} {selection.source} {selection.column}{' '}
            {selection.rateVolumeMode || ''}{' '}
            <b>{selection.direction === 'above' ? t('isAbove') : t('isBelow')}</b>{' '}
            <b>{selection.threshold}</b>{' '}
            {selection.column && (
              <b>
                {selection.rateVolumeMode === 'level'
                  ? String(selectedProject.unitSystem).startsWith('US')
                    ? 'ft'
                    : 'm'
                  : AlertTypeConfigs[selection.alertType].unitsDisplay(
                      selectedProject,
                      selection.column,
                      t
                    )}
              </b>
            )}
            {selection.sites.length > 0
              ? selection.sites.length == 1
                ? ' ' + t('at') + ' ' + selection.sites[0].name + '.'
                : ' ' +
                  t('at') +
                  ' ' +
                  t('anyOfTheAbove') +
                  ' ' +
                  selection.sites.length +
                  ' ' +
                  t('sites').toLowerCase() +
                  '.'
              : '.'}
          </p>
        ) : (
          <p>
            {t('alertSummaryPrefix')} {selection.source} {selection.column}{' '}
            <b>{t('upcomingEventPredicate')}</b>
            {selection.sites.length > 0
              ? selection.sites.length == 1
                ? ' ' + t('at') + ' ' + selection.sites[0].name + '.'
                : ' ' +
                  t('at') +
                  ' ' +
                  t('anyOfTheAbove') +
                  ' ' +
                  selection.sites.length +
                  ' ' +
                  t('sites').toLowerCase() +
                  '.'
              : '.'}
          </p>
        )}
      </div>

      {/* Collapsible CC Recipients section */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setIsRecipientsOpen(!isRecipientsOpen)}
          className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          <span className="mr-1">{isRecipientsOpen ? '-' : '+'}</span>
          {t('additionalRecipients') +
            (selection.ccEmails.length > 0 ? ' (' + selection.ccEmails.length + ')' : '')}
        </button>

        {isRecipientsOpen && (
          <div className="mt-2 ">
            <form onSubmit={addEmail} className="mb-1" noValidate>
              <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md">
                {selection.ccEmails?.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center bg-gray-200 text-gray-800 pl-2 rounded-sm text-sm"
                  >
                    <span>{email}</span>
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      className="ml-1 text-gray-600 hover:text-red-800 hover:bg-red-200 font-bold p-1"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <input
                  ref={emailInputRef}
                  id="ccEmails"
                  type="email"
                  onChange={() => setEmailError(null)}
                  className="flex-grow min-w-[120px] outline-none border-none"
                  placeholder={selection.ccEmails.length === 0 ? t('enterEmails') : ''}
                />
              </div>
            </form>
            {emailError && <p className="text-xs text-red-500">{emailError}</p>}
            <p className="text-xs text-gray-500">{t('pressEnterToAddEmail')}</p>
          </div>
        )}
      </div>
    </>
  );
}
